import { useMutationWithToast } from "@/hooks/shared/use-mutation-with-toast";
import { api } from "@/lib/api-typed";
import type {
  ContentBlock,
  ContentBlockCreate,
} from "@/types";

// ========== Content Block Mutations ==========

export const useCreateContentBlock = () => {
  return useMutationWithToast({
    mutationFn: async (data: {
      parent_type: "training" | "article";
      parent_id: string;
      block: ContentBlockCreate;
    }) => {
      return data.parent_type === "training"
        ? api.post("/trainings/{training_id}/blocks", { path: { training_id: data.parent_id }, body: data.block })
        : api.post("/knowledge-base/{article_id}/blocks", { path: { article_id: data.parent_id }, body: data.block });
    },
    invalidateQueries: (_, variables) => [
      ["content-blocks", variables.parent_type, variables.parent_id],
      ...(variables.parent_type === "training" ? [["trainings", variables.parent_id]] : []),
    ],
  });
};

export const useUpdateContentBlock = () => {
  return useMutationWithToast({
    mutationFn: async (data: {
      id: string;
      parent_type: "training" | "article" | "lesson";
      parent_id: string;
      block_type?: ContentBlock["block_type"];
      content?: Record<string, any>;
      sort_order?: number;
    }) => {
      const { id, parent_type, parent_id, ...updateData } = data;
      return api.patch("/trainings/blocks/{block_id}", { path: { block_id: id }, body: updateData });
    },
    invalidateQueries: (_, variables) => [
      ["content-blocks", variables.parent_type, variables.parent_id],
    ],
  });
};

export const useDeleteContentBlock = () => {
  return useMutationWithToast({
    mutationFn: async (data: {
      id: string;
      parent_type: "training" | "article" | "lesson";
      parent_id: string;
    }) => {
      return api.delete("/trainings/blocks/{block_id}", { path: { block_id: data.id } });
    },
    invalidateQueries: (_, variables) => [
      ["content-blocks", variables.parent_type, variables.parent_id],
    ],
  });
};

export const useReorderContentBlocks = () => {
  return useMutationWithToast({
    mutationFn: async (data: {
      parent_type: "training" | "article" | "lesson";
      parent_id: string;
      block_ids: string[];
    }) => {
      const body = { block_ids: data.block_ids };
      if (data.parent_type === "article") {
        return api.put("/knowledge-base/{article_id}/blocks/reorder", { path: { article_id: data.parent_id }, body });
      }
      if (data.parent_type === "lesson") {
        return api.put("/trainings/lessons/{lesson_id}/blocks/reorder", { path: { lesson_id: data.parent_id }, body });
      }
      return api.put("/trainings/{training_id}/blocks/reorder", { path: { training_id: data.parent_id }, body });
    },
    invalidateQueries: (_, variables) => [
      ["content-blocks", variables.parent_type, variables.parent_id],
    ],
  });
};

// ========== Quiz Mutations ==========

export const useCreateQuizQuestion = () => {
  return useMutationWithToast({
    mutationFn: async (data: {
      block_id: string;
      question_text: string;
      question_type: "multiple_choice" | "true_false" | "open_answer";
      options?: { id: string; text: string; is_correct: boolean }[];
      correct_answer?: string;
      points?: number;
      sort_order?: number;
    }) => {
      const { block_id, ...questionData } = data;
      return api.post("/trainings/blocks/{block_id}/questions", { path: { block_id }, body: questionData });
    },
    invalidateQueries: (_, variables) => [
      ["quiz-questions", variables.block_id],
    ],
  });
};

export const useUpdateQuizQuestion = () => {
  return useMutationWithToast({
    mutationFn: async (data: {
      question_id: string;
      block_id: string;
      question_text?: string;
      question_type?: "multiple_choice" | "true_false" | "open_answer";
      options?: { id: string; text: string; is_correct: boolean }[];
      correct_answer?: string;
      points?: number;
      sort_order?: number;
    }) => {
      const { question_id, block_id, ...updateData } = data;
      return api.patch("/trainings/questions/{question_id}", { path: { question_id }, body: updateData });
    },
    invalidateQueries: (_, variables) => [
      ["quiz-questions", variables.block_id],
    ],
  });
};

export const useDeleteQuizQuestion = () => {
  return useMutationWithToast({
    mutationFn: async (data: { question_id: string; block_id: string }) => {
      return api.delete("/trainings/questions/{question_id}", { path: { question_id: data.question_id } });
    },
    invalidateQueries: (_, variables) => [
      ["quiz-questions", variables.block_id],
    ],
  });
};

export const useSubmitQuizAttempt = () => {
  return useMutationWithToast({
    mutationFn: async (data: {
      block_id: string;
      answers: { question_id: string; answer: string; is_correct: boolean }[];
      score: number;
      max_score: number;
      passed: boolean;
    }) => {
      return api.post("/trainings/blocks/{block_id}/attempt", { path: { block_id: data.block_id }, body: {
          answers: data.answers,
          score: data.score,
          max_score: data.max_score,
          passed: data.passed,
        } });
    },
    invalidateQueries: [
      ["quiz-questions"],
    ],
  });
};

